import { createResizeObserverWithRAF } from '@heidi/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatItem } from '../../../contracts/harness.types';
import type { TurnOperation } from '../../../contracts/turn-identity';

import {
  parseUserPreamble,
  userMessageRendersBubble,
} from '../components/projectChatMessage.helpers';
import type { SubmitProjectTurnArgs } from '../submit-project-turn-args';
import type { LatestTodoPlan } from '../timeline/agent-todo/computeLatestTodoPlan';
import { computePlanProgress } from '../timeline/agent-todo/planProgress';
import { FOLLOW_BOTTOM_THRESHOLD_PX, SNAP_TOP_OFFSET_PX } from './chatScroll.constants';
import { useBottomFade } from './useBottomFade';
import { useDockShrinkNudge } from './useDockShrinkNudge';
import { scrollSignalForItem, useFollowTail } from './useFollowTail';

// pt-3 (12px) + compact dock (32px) + gap (16px).
const PLAN_DOCK_SNAP_TOP_OFFSET_PX = 60;

/** Pixels to add to scrollTop so an anchored block keeps its viewport offset. */
const scrollDeltaToHoldAnchor = (
  viewportTop: number,
  anchorTop: number,
  offsetPx: number
): number | null => {
  const drift = anchorTop - viewportTop - offsetPx;
  if (Math.abs(drift) < 1) return null;
  return drift;
};

// Re-exported so existing importers (useProjectConversationTimeline) keep
// working; drop this once they import from chatScroll.constants directly.
export { SNAP_TOP_OFFSET_PX };

type StartTurnFn = (command: SubmitProjectTurnArgs) => TurnOperation;

// Same visibility rule as `UserBubble`'s null-render guard and
// `ProjectTurnTimeline`'s turn grouping, so this agrees with what
// `getUserMessages` actually finds in the DOM.
const latestVisibleUserMessageId = (items: ChatItem[]): string | null => {
  const firstUserMessageId = items.find((entry) => entry.kind === 'user_message')?.id;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (!item || item.kind !== 'user_message') continue;
    if (
      userMessageRendersBubble(
        parseUserPreamble(item.text, {
          isRoutineRunSeed: item.id === firstUserMessageId,
        }),
        item.attachments?.length ?? 0
      )
    ) {
      return item.id;
    }
  }
  return null;
};

interface UseProjectChatScrollArgs {
  items: ChatItem[];
  latestTurnHasReply: boolean;
  showThinking: boolean;
  turnInFlight: boolean;
  hasPendingApproval: boolean;
  /** The active run, or undefined before it's known. A refused start
   *  never produces a user message, so we don't arm a snap that could
   *  never be consumed. */
  agentRunId?: string;
  /**
   * Required for Project chat send-snap. Omitted only by the RPA overlay
   * read-only transcript (`useProjectConversationTimeline` without a send).
   */
  startTurn?: StartTurnFn;
  latestTodoPlan?: LatestTodoPlan | null;
  showPlanProgressDock?: boolean;
  /**
   * Start out following the streaming tail. Off for Project chat, which holds
   * the snapped question at the top and offers a jump instead. On for the RPA
   * overlay, a passive read-only status view of a run in progress where
   * watching the tail IS the surface.
   */
  followTailByDefault?: boolean;
}

export const useProjectChatScroll = ({
  items,
  latestTurnHasReply,
  showThinking,
  turnInFlight,
  hasPendingApproval,
  agentRunId,
  startTurn,
  latestTodoPlan = null,
  showPlanProgressDock = true,
  followTailByDefault = false,
}: UseProjectChatScrollArgs) => {
  const latestUserMessageId = latestVisibleUserMessageId(items);
  const planAnchorIndex = latestTodoPlan
    ? items.findIndex((item) => item.id === latestTodoPlan.anchorId)
    : -1;
  const latestUserIndex = latestUserMessageId
    ? items.findIndex((item) => item.id === latestUserMessageId)
    : -1;
  const hasPlanDockInset =
    showPlanProgressDock &&
    latestTodoPlan !== null &&
    !computePlanProgress(latestTodoPlan.state).isComplete &&
    planAnchorIndex >= 0 &&
    latestUserIndex > planAnchorIndex;
  const effectiveSnapTopOffsetPx = hasPlanDockInset
    ? PLAN_DOCK_SNAP_TOP_OFFSET_PX
    : SNAP_TOP_OFFSET_PX;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Fallback scroll target. `followTailRef` (the sentinel inside
  // ProjectTurnTimeline) is the primary target, but it isn't mounted during
  // the seeded-start window — before `agentRunId`/`renderItem` exist the shell
  // renders the thinking block directly under `contentRef` with no timeline —
  // so keep contentRef as the target for that path. Not dead code.
  const contentRef = useRef<HTMLDivElement>(null);
  const followTailRef = useRef<HTMLDivElement>(null);
  // The always-mounted scroll content wrapper. We watch its height with a
  // ResizeObserver so follow-tail reacts to ANY rendered growth, not just the
  // item fields `scrollSignalForItem` happens to hash — e.g. a draft/document
  // streaming into a tool's `input`, a sub-agent's child stream, an expanding
  // sources card, or async-loading attachments. The content hash alone can't
  // see those, which is why streaming into a draft card used to stall follow.
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const { bottomFadeRef, updateFadeOpacity } = useBottomFade({
    scrollRef,
    getTailEl: () => followTailRef.current ?? contentRef.current,
  });
  const [scrollViewportHeight, setScrollViewportHeight] = useState<number | null>(null);
  // Jump-to-bottom affordance state, mirroring Evidence's `useAutoScroll`:
  // `hasContentBelow` is the measurement, `hasReachedBottomSinceTurn` is the
  // "the reader has already been down there" latch that stops the button
  // reappearing once they have caught up on a settled turn. Only a reader
  // gesture raises that latch — a user scroll landing at the rendered tail, or
  // `scrollToBottom` — never a measurement that happens to find nothing below.
  const [hasContentBelow, setHasContentBelow] = useState(false);
  const [hasReachedBottomSinceTurn, setHasReachedBottomSinceTurn] = useState(true);

  // Transcript scroll state.
  //   `isFollowingRef`  — are we sticking to the streaming tail? Off by
  //                       default, and a send does NOT turn it on: the
  //                       question stays pinned at the top and the reply
  //                       streams in below the fold, with the
  //                       jump-to-bottom affordance as the way down
  //                       (matches Evidence chat, `useAutoScroll`).
  //                       Only the user parking the scroller at the
  //                       bottom themselves re-arms it.
  //   `snapPendingRef`  — armed by a fresh send; the next render pins
  //                       that question to the top exactly once.
  //   `ignoreScrollRef` — set around our own programmatic scrolls so the
  //                       follow listener doesn't read them as the user
  //                       changing intent.
  const isFollowingRef = useRef(followTailByDefault);
  const snapPendingRef = useRef(false);
  const snapUserCountBeforeRef = useRef<number | null>(null);
  const snapLatestUserIdBeforeRef = useRef<string | null>(null);
  const ignoreScrollRef = useRef(false);
  // Detects a fresh turnInFlight false→true edge — a turn just started,
  // locally or on another device. Seeded to the mount value so opening an
  // already-running conversation isn't read as "just started".
  const prevTurnInFlightRef = useRef(turnInFlight);
  // id of the user message already snapped for, so the same turn can't
  // re-arm once turnInFlight catches up on a later render. Identity-based
  // rather than a count: a pre-output cancel can remove a message from
  // `items` (`dropEmptyCancelledTurns`), and a count-based high-water mark
  // would then wrongly block the next turn's snap once the count caught back
  // up to where it used to be.
  const lastSnappedUserMessageIdRef = useRef<string | null>(null);
  // This hook stays mounted across a run switch, so the per-run state above
  // needs an explicit reset (mirrors `useLatestTurnFloor`'s latch reset).
  // `isFollowingRef` is left alone — scroll position isn't run-scoped.
  const prevAgentRunIdRef = useRef(agentRunId);
  if (prevAgentRunIdRef.current !== agentRunId) {
    prevAgentRunIdRef.current = agentRunId;
    lastSnappedUserMessageIdRef.current = null;
    prevTurnInFlightRef.current = turnInFlight;
    snapPendingRef.current = false;
    snapUserCountBeforeRef.current = null;
    snapLatestUserIdBeforeRef.current = null;
  }

  // Scroll the transcript without tripping the follow listener. The
  // browser dispatches the resulting `scroll` event in the same frame
  // *before* rAF callbacks, so the listener sees the flag and bails; the
  // rAF clear is a fallback for scrolls that don't move the viewport.
  const scrollProgrammatically = useCallback((run: () => void) => {
    ignoreScrollRef.current = true;
    run();
    requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  }, []);

  // A scrollTop is only meaningful at a fixed width. Opening the canvas
  // rewraps this column, so remember the block on screen and restore it.
  const scrollAnchorRef = useRef<{ el: HTMLElement; offsetPx: number } | null>(null);

  const captureScrollAnchor = useCallback(() => {
    const scroller = scrollRef.current;
    const content = scrollContentRef.current;
    if (!scroller || !content) return;
    const viewportTop = scroller.getBoundingClientRect().top;
    // Direct children are turn-sized blocks. Stop at the first one still on screen.
    for (const child of Array.from(content.children)) {
      if (!(child instanceof HTMLElement)) continue;
      const rect = child.getBoundingClientRect();
      if (rect.bottom > viewportTop) {
        scrollAnchorRef.current = { el: child, offsetPx: rect.top - viewportTop };
        return;
      }
    }
    scrollAnchorRef.current = null;
  }, []);

  const restoreScrollAnchor = useCallback(() => {
    const scroller = scrollRef.current;
    const anchor = scrollAnchorRef.current;
    if (!scroller || !anchor || !anchor.el.isConnected) return;
    const drift = scrollDeltaToHoldAnchor(
      scroller.getBoundingClientRect().top,
      anchor.el.getBoundingClientRect().top,
      anchor.offsetPx
    );
    if (drift === null) return;
    scrollProgrammatically(() => {
      scroller.scrollTo({ top: scroller.scrollTop + drift });
    });
  }, [scrollProgrammatically]);

  // Measure from the rendered tail, not the latest-turn min-height bottom, so
  // both follow intent and the jump-to-bottom affordance track what the user
  // can actually see — the min-height reserve below the tail sentinel is empty
  // room, not content.
  const distanceFromRenderedTail = useCallback((scroller: HTMLElement): number => {
    const tail = followTailRef.current ?? contentRef.current;
    return tail
      ? tail.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom
      : scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  }, []);

  const measureContentBelow = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    setHasContentBelow(distanceFromRenderedTail(scroller) > FOLLOW_BOTTOM_THRESHOLD_PX);
  }, [distanceFromRenderedTail]);

  const { onScrollerResize } = useDockShrinkNudge({
    scrollRef,
    snapPendingRef,
    scrollProgrammatically,
  });

  const getUserMessages = useCallback((scroller: HTMLElement) => {
    return scroller.querySelectorAll<HTMLElement>('[data-user-message]');
  }, []);

  const scrollUserMessageToTop = useCallback(
    (scroller: HTMLElement, userMessage: HTMLElement) => {
      const scrollerRect = scroller.getBoundingClientRect();
      const userRect = userMessage.getBoundingClientRect();
      const targetTop = Math.max(
        scroller.scrollTop + userRect.top - scrollerRect.top - effectiveSnapTopOffsetPx,
        0
      );
      scroller.scrollTo({ top: targetTop });
    },
    [effectiveSnapTopOffsetPx]
  );

  const armSnap = useCallback(() => {
    if (!agentRunId) return;
    const scroller = scrollRef.current;
    snapUserCountBeforeRef.current = scroller ? getUserMessages(scroller).length : null;
    snapLatestUserIdBeforeRef.current = latestVisibleUserMessageId(items);
    snapPendingRef.current = true;
  }, [agentRunId, getUserMessages, items]);

  const disarmSnap = useCallback(() => {
    snapPendingRef.current = false;
    snapUserCountBeforeRef.current = null;
    snapLatestUserIdBeforeRef.current = null;
  }, []);

  const startTurnWithScrollSnap = useCallback<StartTurnFn>(
    (command) => {
      if (!startTurn) {
        throw new Error(
          'Project chat scroll startTurn is omitted only on the read-only RPA overlay'
        );
      }
      armSnap();
      const operation = startTurn(command);
      void operation.accepted.catch(() => {
        disarmSnap();
      });
      return operation;
    },
    [armSnap, disarmSnap, startTurn]
  );

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const updateHeight = () => {
      setScrollViewportHeight(scroller.clientHeight || null);
      updateFadeOpacity();
      measureContentBelow();
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') return;

    // Track the scroller's own height so we can tell a viewport SHRINK (the
    // composer dock below growing) apart from a grow.
    let prevHeight = scroller.clientHeight;
    let prevWidth = scroller.clientWidth;

    const observer = createResizeObserverWithRAF(() => {
      const nextHeight = scroller.clientHeight;
      const nextWidth = scroller.clientWidth;
      const shrankBy = prevHeight - nextHeight;
      const widthChanged = nextWidth !== prevWidth;
      prevHeight = nextHeight;
      prevWidth = nextWidth;
      updateHeight();
      if (widthChanged) {
        restoreScrollAnchor();
      }
      onScrollerResize(shrankBy);
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [updateFadeOpacity, onScrollerResize, measureContentBelow, restoreScrollAnchor]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const onScroll = () => {
      updateFadeOpacity();
      captureScrollAnchor();
      const atRenderedTail =
        distanceFromRenderedTail(scroller) <= FOLLOW_BOTTOM_THRESHOLD_PX;
      setHasContentBelow(!atRenderedTail);
      if (ignoreScrollRef.current) {
        ignoreScrollRef.current = false;
        return;
      }
      isFollowingRef.current = atRenderedTail;
      if (atRenderedTail) setHasReachedBottomSinceTurn(true);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    updateFadeOpacity();
    measureContentBelow();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [
    updateFadeOpacity,
    measureContentBelow,
    distanceFromRenderedTail,
    captureScrollAnchor,
  ]);

  // Tries to resolve a pending send-snap for the current render. Returns
  // true whenever snap has any claim on this pass — still pending (bubble
  // not painted yet, or the pre-send count hasn't increased) or just
  // resolved — so the arbitration effect below can make "snap wins" a
  // plain early return instead of coordinating through a cross-effect ref.
  const tryResolveSnap = useCallback(
    (scroller: HTMLElement): boolean => {
      // A turn started without this device arming the snap itself (that
      // happens synchronously at send time, before turnInFlight can flip) —
      // treat it as a remote turn and give it the same snap-to-top treatment.
      // No baseline needed: the new message is already rendered by the time
      // this edge is observed, one render behind the snapshot that flips
      // turnInFlight.
      const turnJustStarted = !prevTurnInFlightRef.current && turnInFlight;
      prevTurnInFlightRef.current = turnInFlight;
      if (turnJustStarted && !snapPendingRef.current) {
        const currentUserMessageId = latestVisibleUserMessageId(items);
        if (
          currentUserMessageId &&
          currentUserMessageId !== lastSnappedUserMessageIdRef.current
        ) {
          snapPendingRef.current = true;
          snapUserCountBeforeRef.current = null;
        }
      }

      if (!snapPendingRef.current) return false;

      const userMessages = getUserMessages(scroller);
      const lastUser = userMessages[userMessages.length - 1] ?? null;
      // Bubble not painted yet — wait for the next timeline/state change.
      // Still pending, so this pass belongs to the snap.
      if (!lastUser) return true;
      if (
        snapUserCountBeforeRef.current !== null &&
        userMessages.length <= snapUserCountBeforeRef.current
      ) {
        const currentUserMessageId = latestVisibleUserMessageId(items);
        const previewReplacedInPlace =
          currentUserMessageId != null &&
          snapLatestUserIdBeforeRef.current != null &&
          currentUserMessageId !== snapLatestUserIdBeforeRef.current;
        if (!previewReplacedInPlace) {
          return true;
        }
      }

      snapPendingRef.current = false;
      snapUserCountBeforeRef.current = null;
      snapLatestUserIdBeforeRef.current = null;
      lastSnappedUserMessageIdRef.current = latestVisibleUserMessageId(items);
      // The question is now at the top and the reply will stream in below the
      // fold; the reader has not been down there for this turn. Follow goes
      // back to the surface's default rather than off outright, so the overlay
      // keeps watching the tail after its snap.
      isFollowingRef.current = followTailByDefault;
      setHasReachedBottomSinceTurn(false);
      scrollProgrammatically(() => {
        scrollUserMessageToTop(scroller, lastUser);
      });
      return true;
    },
    [
      items,
      turnInFlight,
      getUserMessages,
      scrollUserMessageToTop,
      scrollProgrammatically,
      followTailByDefault,
    ]
  );

  const { scrollToRenderedTail } = useFollowTail({
    scrollRef,
    scrollContentRef,
    followTailRef,
    contentRef,
    isFollowingRef,
    snapPendingRef,
    scrollProgrammatically,
  });

  // Content hash that drives the arbitration effect below. Computed every
  // render ON PURPOSE: the transport mutates the items array IN PLACE as a
  // reply streams (its reference is stable), so a `useMemo` keyed on
  // `items` would never see the growth and follow-tail would stall
  // mid-stream. The walk is O(items); cheap next to the per-token
  // re-renders it guards.
  const renderedTailSignal = items.map(scrollSignalForItem).join('|');

  // The one place that decides who acts on a tail-signal change: snap and
  // follow are each a pure "do your thing" function (tryResolveSnap,
  // scrollToRenderedTail) with no awareness of each other; this effect owns
  // the choice between them, the same way the pre-split effect did before
  // useFollowTail and useSendSnap existed as separate hooks.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    if (tryResolveSnap(scroller)) {
      measureContentBelow();
      return;
    }

    if (isFollowingRef.current) {
      scrollProgrammatically(() => {
        scrollToRenderedTail(scroller);
      });
    }

    measureContentBelow();
  }, [
    items,
    renderedTailSignal,
    latestTurnHasReply,
    showThinking,
    turnInFlight,
    hasPendingApproval,
    tryResolveSnap,
    scrollToRenderedTail,
    scrollProgrammatically,
    measureContentBelow,
  ]);

  const scrollToBottom = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    // Hold position from here rather than resuming tail-follow: jumping down
    // is a request to see what has arrived, not a request to be dragged along
    // by everything that arrives next. An instant scroll (rather than the
    // smooth one this used to do) is what makes that stick — a smooth scroll
    // emits scroll events for the whole animation and only the first is
    // suppressed, so the listener above would read the landing as the user
    // parking at the bottom and re-arm follow.
    isFollowingRef.current = false;
    setHasContentBelow(false);
    setHasReachedBottomSinceTurn(true);
    scrollProgrammatically(() => {
      scroller.scrollTo({ top: scroller.scrollHeight });
    });
  }, [scrollProgrammatically]);

  // Same rule as Evidence's `useAutoScroll`: offer the jump while the turn is
  // live, and afterwards only until the reader has actually been to the bottom
  // — so it doesn't reappear when they scroll back up through a settled turn.
  const hasMoreContentBelow =
    hasContentBelow && (turnInFlight || !hasReachedBottomSinceTurn);

  return {
    contentRef,
    followTailRef,
    hasMoreContentBelow,
    scrollContentRef,
    scrollRef,
    bottomFadeRef,
    scrollViewportHeight,
    snapTopOffsetPx: effectiveSnapTopOffsetPx,
    startTurn: startTurnWithScrollSnap,
    scrollToBottom,
  };
};
